/**
 * Smart Model Routing
 * Analyzes user queries and suggests the best provider/model.
 */

/**
 * Routing rules — ordered by priority. First match wins.
 * Each rule has: patterns (regex), category label, suggested provider+model, reason.
 */
const ROUTING_RULES = [
  {
    id: 'code',
    patterns: [
      /\b(code|function|class|bug|debug|error|compile|syntax|refactor|implement|programming|algorithm|api|endpoint|database|sql|html|css|javascript|python|java|rust|go|typescript|regex|script)\b/i,
      /```/,
      /\b(fix|write|create|build)\b.*\b(code|app|program|function|script|component)\b/i,
    ],
    provider: 'google',
    model: 'claude-opus-4-6-thinking',
    label: 'Code & Programming',
    reason: 'Claude Opus 4.6 for complex coding',
  },
  {
    id: 'reasoning',
    patterns: [
      /\b(math|equation|calculus|algebra|geometry|proof|theorem|solve|calculate|derivative|integral|statistics|probability|logic|logical)\b/i,
      /\b(step[- ]by[- ]step|chain of thought|reason|reasoning|think through)\b/i,
      /[=+\-*/^√∫∑∏]\s*\d/,
      /\d+\s*[+\-*/^]\s*\d+/,
    ],
    provider: 'deepseek',
    model: 'deepseek-reasoner',
    label: 'Math & Reasoning',
    reason: 'DeepSeek Reasoner for math/logic',
  },
  {
    id: 'creative',
    patterns: [
      /\b(write|story|poem|creative|fiction|novel|essay|blog|article|copywriting|lyrics|screenplay|narrative|haiku|sonnet)\b/i,
      /\b(imagine|brainstorm|creative writing|storytelling)\b/i,
    ],
    provider: 'google',
    model: 'claude-sonnet-4-5',
    label: 'Creative Writing',
    reason: 'Claude Sonnet 4.5 for creative tasks',
  },
  {
    id: 'analysis',
    patterns: [
      /\b(analyze|analysis|compare|comparison|evaluate|review|assess|critique|pros and cons|trade.?offs?)\b/i,
      /\b(explain|breakdown|deep dive|in.?depth)\b/i,
    ],
    provider: 'google',
    model: 'claude-sonnet-4-5-thinking',
    label: 'Analysis & Explanation',
    reason: 'Claude Sonnet 4.5 (Thinking) for analysis',
  },
  {
    id: 'quick',
    patterns: [
      /\b(what is|who is|when did|where is|how many|define|meaning of|translate|convert|list)\b/i,
      /^.{0,40}$/,  // Very short simple queries only
    ],
    provider: 'google',
    model: 'gemini-3-flash',
    label: 'Quick Answer',
    reason: 'Gemini 3 Flash for fast responses',
  },
];

/**
 * Analyze user input and suggest the best model
 * @param {string} query - User's message
 * @param {Function} isProviderAvailable - Check if provider is configured
 * @returns {{ provider: string, model: string, label: string, reason: string } | null}
 */
export function suggestModel(query, isProviderAvailable) {
  if (!query || query.length < 3) return null;

  for (const rule of ROUTING_RULES) {
    const matched = rule.patterns.some(pattern => pattern.test(query));
    if (matched) {
      // Check if the suggested provider is available
      if (isProviderAvailable && !isProviderAvailable(rule.provider)) {
        continue; // Skip to next rule if provider not available
      }
      return {
        provider: rule.provider,
        model: rule.model,
        label: rule.label,
        reason: rule.reason,
      };
    }
  }

  return null;
}

/**
 * Get all routing rules (for settings display)
 */
export function getRoutingRules() {
  return ROUTING_RULES.map(r => ({
    id: r.id,
    label: r.label,
    provider: r.provider,
    model: r.model,
    reason: r.reason,
  }));
}
